import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

const TABS = [
  { id: "details", label: "GO Details" },
  { id: "material", label: "Material" },
  { id: "quantities", label: "Quantities & Stock" },
];

const WHOSE_STOCK_OPTIONS = ["Client", "InstaTenders"];

const emptyHeader = () => ({
  goNumber: "",
  goDate: new Date().toISOString().slice(0, 10),
  goSummary: "",
  invoiceNumber: "",
  invoiceDate: "",
  whoseStock: "InstaTenders",
  soNumber: "",
  soDate: "",
  customer: "",
  customerName: "",
  customerId: "",
  username: String(localStorage.getItem("name") || "").trim(),
  clientReference: "",
  department: "",
});

const createMaterialRow = (overrides = {}) => ({
  rowKey: `go-mat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sourceKey: "",
  sourceType: "material",
  materialCode: "",
  materialDescription: "",
  hsnCode: "",
  uom: "",
  soQuantity: "",
  availableStock: "",
  goodsOutwards: "",
  ...overrides,
});

const toDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const toNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
};

const qtyString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
};

const parseQtyNum = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const computeUpdatedAvailableStock = (row) => {
  const available = parseFloat(row?.availableStock);
  const outwards = parseFloat(row?.goodsOutwards);
  const a = Number.isFinite(available) ? available : 0;
  const o = Number.isFinite(outwards) ? outwards : 0;
  return String(a - o);
};

const cleanDash = (value) => {
  const s = value == null ? "" : String(value).trim();
  return !s || s === "-" ? "" : s;
};

const isStandaloneMaterialRow = (row) => {
  const source = String(row?.sourceType || "").toLowerCase();
  if (source === "so") return false;
  if (row?.so_id || row?.soId) return false;
  return true;
};

/** Convert API dates (DD-MM-YYYY or ISO) to YYYY-MM-DD for <input type="date"> */
const toDateInputValue = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return "";
};

const extractEntityId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(value._id || value.id || value.$oid || "").trim();
  }
  return "";
};

const getHeaderSnapshot = (header) =>
  JSON.stringify({
    goDate: String(header?.goDate || "").trim(),
    goSummary: String(header?.goSummary || "").trim(),
    invoiceNumber: String(header?.invoiceNumber || "").trim(),
    invoiceDate: String(header?.invoiceDate || "").trim(),
    whoseStock: String(header?.whoseStock || "").trim(),
    soNumber: String(header?.soNumber || "").trim(),
    soDate: String(header?.soDate || "").trim(),
    customer: String(header?.customerName || header?.customer || "").trim(),
    customerId: String(header?.customerId || "").trim(),
    username: String(header?.username || "").trim(),
    clientReference: String(header?.clientReference || "").trim(),
    department: String(header?.department || "").trim(),
  });

const isSameHeaderSnapshot = (a, b) => a === b;

const extractCreatedGoId = (payload) => {
  const candidates = [
    payload?.data?._id,
    payload?.data?.id,
    payload?.data?.go?._id,
    payload?.go?._id,
    payload?._id,
  ];
  return candidates.find((id) => id != null && String(id).trim() !== "") || null;
};

const extractGoNumber = (payload) => {
  const candidates = [
    payload?.data?.goNumber,
    payload?.goNumber,
    payload?.data?.go?.goNumber,
    payload?.data?.nextGoNumber,
  ];
  return candidates.find((n) => n != null && String(n).trim() !== "") || null;
};

function pickGoFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (Array.isArray(dataBlock.goodsOutwards) && dataBlock.goodsOutwards[0]) {
    return dataBlock.goodsOutwards[0];
  }
  if (dataBlock.goodsOutwards && typeof dataBlock.goodsOutwards === "object") {
    return dataBlock.goodsOutwards;
  }
  if (Array.isArray(dataBlock) && dataBlock[0]) {
    return dataBlock[0];
  }
  if (dataBlock.go && typeof dataBlock.go === "object") {
    return dataBlock.go;
  }
  if (
    !Array.isArray(dataBlock) &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.goNumber != null)
  ) {
    return dataBlock;
  }
  return null;
}

const extractMaterialList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data?.materials)) return data.materials;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.materialList)) return data.materialList;
  if (Array.isArray(data)) return data;
  if (Array.isArray(payload?.materials)) return payload.materials;
  return [];
};

const extractCustomersList = (payload) => {
  const data = payload?.data;
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data) && data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    [];
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || "").trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || "",
      ).trim(),
      status: String(item?.status || "").trim().toLowerCase(),
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
};

const extractSoList = (payload) => {
  if (Array.isArray(payload?.data?.salesOrders)) return payload.data.salesOrders;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const pickInventoryRecord = (responseData) => {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (typeof dataBlock === "string") dataBlock = parseJsonIfString(dataBlock);
  if (!dataBlock || typeof dataBlock !== "object") return null;
  if (dataBlock.inventory && typeof dataBlock.inventory === "object") {
    return dataBlock.inventory;
  }
  if (
    dataBlock.materialCode != null ||
    dataBlock.stockQuantity != null ||
    dataBlock.stock != null
  ) {
    return dataBlock;
  }
  if (Array.isArray(dataBlock.inventories) && dataBlock.inventories.length) {
    return dataBlock.inventories[0];
  }
  return null;
};

const pickStockQuantity = (inventory) => {
  if (!inventory) return "";
  const raw =
    inventory.stockQuantity ??
    inventory.availableStock ??
    inventory.stock ??
    inventory.currentStock ??
    inventory.quantity ??
    "";
  return qtyString(raw);
};

const buildSoBalanceRequestLine = (m) => ({
  materialCode: cleanDash(m?.materialCode),
  materialDescription: m?.materialDescription || "",
  hsnCode: cleanDash(m?.hsn || m?.hsnCode),
  uom: cleanDash(m?.uom),
  soQuantity: qtyString(m?.quantity ?? m?.soQuantity ?? 0),
});

const mapSoReservations = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((r, i) => ({
    key: String(r?.goId || i),
    goId: String(r?.goId || "").trim(),
    goNumber: String(r?.goNumber || "").trim(),
    goDate: r?.goDate || "",
    status: String(r?.status || "").trim().toLowerCase(),
    goodsOutwards: qtyString(r?.goodsOutwards ?? 0),
    createdByName:
      (r?.createdBy && typeof r.createdBy === "object"
        ? r.createdBy.name || r.createdBy.email
        : r?.createdBy) || "",
  }));
};

const getGoReservationCredit = (reservations, goId, fallbackOutwards = 0) => {
  const id = String(goId || "").trim();
  if (id && Array.isArray(reservations) && reservations.length) {
    const mine = reservations.find((r) => String(r?.goId || "").trim() === id);
    if (mine) return parseQtyNum(mine.goodsOutwards);
  }
  return parseQtyNum(fallbackOutwards);
};

const withEditableSoBalance = (apiBalance, credit) =>
  qtyString(parseQtyNum(apiBalance) + parseQtyNum(credit));

const mapSoBalanceMaterial = (m, index = 0, options = {}) => {
  const { excludeGoId = "", creditFallback = 0 } = options;
  const code = cleanDash(m?.materialCode);
  const description = m?.materialDescription || "";
  const allReservations = mapSoReservations(m?.reservations);
  const otherDraftReservations = allReservations.filter(
    (r) => r.status === "draft" && String(r.goId) !== String(excludeGoId || ""),
  );
  const credit = excludeGoId
    ? getGoReservationCredit(allReservations, excludeGoId, creditFallback)
    : 0;

  return {
    sourceKey: `${code || "mat"}|${description}|${index}`,
    materialCode: code,
    materialDescription: description,
    hsnCode: cleanDash(m?.hsnCode),
    uom: cleanDash(m?.uom),
    soQuantity: qtyString(m?.soQuantity),
    soBalanceQuantity: withEditableSoBalance(m?.soBalanceQuantity, credit),
    availableStock: qtyString(m?.availableStock ?? m?.stockQuantity ?? ""),
    hasDraftReservation: otherDraftReservations.length > 0,
    reservations: otherDraftReservations,
  };
};

const buildGoRowFromSoLine = (m) =>
  createMaterialRow({
    sourceKey: m.sourceKey,
    sourceType: "so",
    materialCode: m.materialCode || "",
    materialDescription: m.materialDescription || "",
    hsnCode: m.hsnCode || "",
    uom: m.uom || "",
    soQuantity: m.soQuantity || "",
    soBalanceQuantity: m.soBalanceQuantity || "",
    availableStock: m.availableStock || "",
    goodsOutwards: "",
  });

const formatReservationDate = (value) => {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
};

function GoodsOutwardsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const soSearchRef = useRef(null);
  const [activeTab, setActiveTab] = useState("details");
  const [form, setForm] = useState(() => emptyHeader());
  const [materials, setMaterials] = useState([]);
  const materialsRef = useRef(materials);
  materialsRef.current = materials;
  const [goId, setGoId] = useState(null);
  const [goStatus, setGoStatus] = useState("draft");
  const [savedHeaderSnapshot, setSavedHeaderSnapshot] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingGo, setIsLoadingGo] = useState(true);

  const [stockLoadingKeys, setStockLoadingKeys] = useState({});
  const [customers, setCustomers] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const [materialMasterCatalog, setMaterialMasterCatalog] = useState([]);
  const [isLoadingMaterialMaster, setIsLoadingMaterialMaster] = useState(false);
  const [materialCodeDropdownRowKey, setMaterialCodeDropdownRowKey] =
    useState(null);
  const [materialCodeDropdownRect, setMaterialCodeDropdownRect] = useState(null);
  const materialMasterLoadedRef = useRef(false);
  const materialCodeInputRefs = useRef({});

  const [soSearchTerm, setSoSearchTerm] = useState("");
  const [soResults, setSoResults] = useState([]);
  const [isSearchingSo, setIsSearchingSo] = useState(false);
  const [showSoDropdown, setShowSoDropdown] = useState(false);
  const [isLoadingSoDetail, setIsLoadingSoDetail] = useState(false);
  const [soMaterials, setSoMaterials] = useState([]);
  const [selectedSoKeys, setSelectedSoKeys] = useState([]);
  const [soMaterialSearchApplied, setSoMaterialSearchApplied] = useState("");
  const [soMaterialResultsOpen, setSoMaterialResultsOpen] = useState(true);

  const isHeaderDirty = useCallback(
    () => !isSameHeaderSnapshot(savedHeaderSnapshot, getHeaderSnapshot(form)),
    [savedHeaderSnapshot, form],
  );

  const updateForm = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleCustomerChange = (customerId) => {
    const selected = customers.find((c) => c._id === customerId);
    const name = selected?.customerName || "";
    setForm((prev) => ({
      ...prev,
      customerId: customerId || "",
      customer: name,
      customerName: name,
    }));
    setFieldErrors((prev) => {
      if (!prev.customerId && !prev.customerName) return prev;
      const next = { ...prev };
      delete next.customerId;
      delete next.customerName;
      delete next.customer;
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const fetchCustomers = async () => {
      setIsLoadingCustomers(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-all-customers",
          page: 1,
          limit: 5000,
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;
        if (isApiSuccess(responseData)) {
          setCustomers(extractCustomersList(responseData));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch customers error:", error);
        }
      } finally {
        if (!cancelled) setIsLoadingCustomers(false);
      }
    };
    fetchCustomers();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve customerId from name when record only had customerName
  useEffect(() => {
    if (!customers.length) return;
    const currentId = String(form.customerId || "").trim();
    if (currentId && customers.some((c) => c._id === currentId)) return;

    const name = String(form.customerName || form.customer || "")
      .trim()
      .toLowerCase();
    if (!name) return;

    const match = customers.find(
      (c) => c.customerName.toLowerCase() === name,
    );
    if (!match) return;

    setForm((prev) => ({
      ...prev,
      customerId: match._id,
      customer: match.customerName,
      customerName: match.customerName,
    }));
  }, [customers, form.customerId, form.customerName, form.customer]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (soSearchRef.current && !soSearchRef.current.contains(e.target)) {
        setShowSoDropdown(false);
      }
      const inMaterialCodeSearch = e.target.closest?.(
        "[data-go-material-code-search='true']",
      );
      const inMaterialCodeDropdown = e.target.closest?.(
        "[data-go-material-code-dropdown='true']",
      );
      if (!inMaterialCodeSearch && !inMaterialCodeDropdown) {
        setMaterialCodeDropdownRowKey(null);
        setMaterialCodeDropdownRect(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);


  const resolveSoIdByNumber = useCallback(async (soNumber) => {
    const q = String(soNumber || "").trim();
    if (!q) return "";
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-sales-order",
        page: 1,
        limit: 20,
        search: q,
        status: "Active",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) return "";
      const list = extractSoList(responseData);
      const match =
        list.find(
          (s) => String(s.cpoNumber || "").trim().toLowerCase() === q.toLowerCase(),
        ) || list[0];
      return String(match?.id || match?._id || "").trim();
    } catch (error) {
      console.error("Resolve SO id by number error:", error);
      return "";
    }
  }, []);

  const loadSoBalanceLines = useCallback(
    async (soNumber, currentGoId) => {
      if (!soNumber) {
        setSoMaterials([]);
        return;
      }
      setIsLoadingSoDetail(true);
      try {
        const currentMaterials = materialsRef.current;
        const soId = await resolveSoIdByNumber(soNumber);
        let soLines = [];
        if (soId) {
          const detailRes = await axios.post(API_URL, {
            action: "get-sales-order-by-id",
            id: soId,
          });
          const detailData = normalizeApiPayload(parseApiResponse(detailRes.data));
          if (isApiSuccess(detailData)) {
            soLines = Array.isArray(detailData?.data?.materials)
              ? detailData.data.materials
              : [];
          }
        }
        if (!soLines.length) {
          soLines = currentMaterials
            .filter((row) => String(row.sourceType || "").toLowerCase() === "so")
            .map((row) => ({
              materialCode: row.materialCode,
              materialDescription: row.materialDescription,
              hsn: row.hsnCode,
              uom: row.uom,
              quantity: row.soQuantity,
            }));
        }
        if (!soLines.length) {
          setSoMaterials([]);
          return;
        }

        const balanceRes = await axios.post(API_URL, {
          action: "get-so-material-balance",
          soNumber,
          materials: soLines.map(buildSoBalanceRequestLine),
        });
        const balanceData = normalizeApiPayload(parseApiResponse(balanceRes.data));
        if (!isApiSuccess(balanceData)) {
          console.warn("get-so-material-balance failed:", balanceData?.message);
          setSoMaterials([]);
          return;
        }
        const balanceMats = Array.isArray(balanceData?.data?.materials)
          ? balanceData.data.materials
          : [];

        const mappedMats = balanceMats.map((m, i) => {
          const existingRow = currentMaterials.find(
            (row) =>
              String(row.materialCode || "") === String(m.materialCode || "") &&
              String(row.materialDescription || "") ===
                String(m.materialDescription || ""),
          );
          return mapSoBalanceMaterial(m, i, {
            excludeGoId: currentGoId,
            creditFallback: existingRow?.goodsOutwards || 0,
          });
        });

        setSoMaterials(mappedMats);

        setMaterials((prev) =>
          prev.map((row) => {
            const match = mappedMats.find(
              (m) =>
                String(m.materialCode || "") === String(row.materialCode || "") &&
                String(m.materialDescription || "") ===
                  String(row.materialDescription || ""),
            );
            if (!match) return row;
            return { ...row, soBalanceQuantity: match.soBalanceQuantity };
          }),
        );
      } catch (error) {
        console.error("Load SO balance error:", error);
        setSoMaterials([]);
      } finally {
        setIsLoadingSoDetail(false);
      }
    },
    [resolveSoIdByNumber],
  );

  useEffect(() => {
    let cancelled = false;

    const fetchGoForEdit = async () => {
      if (!id) {
        toast.error("Valid id is required", { autoClose: 2500 });
        navigate("/goodsOutwards");
        return;
      }

      setIsLoadingGo(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-goods-outwards-by-id",
          id,
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;

        if (!isApiSuccess(responseData)) {
          toast.error(
            responseData?.message || "Failed to fetch goods outwards",
            { autoClose: 3000 },
          );
          navigate("/goodsOutwards");
          return;
        }

        const record = pickGoFromResponse(responseData);
        if (!record) {
          toast.error("Goods outwards not found", { autoClose: 3000 });
          navigate("/goodsOutwards");
          return;
        }

        const customerId = extractEntityId(record.customerId);
        const customerName = String(
          record.customerName ||
            record.customer ||
            record.customerId?.customerName ||
            "",
        ).trim();
        const whoseStock =
          record.whoseStock ||
          (Array.isArray(record.materials) && record.materials[0]?.whoseStock) ||
          "InstaTenders";

        const nextForm = {
          goNumber: record.goNumber || "",
          goDate: toDateInputValue(record.goDate) || emptyHeader().goDate,
          goSummary: record.goSummary || "",
          invoiceNumber: record.invoiceNumber || "",
          invoiceDate: toDateInputValue(record.invoiceDate),
          whoseStock: String(whoseStock || "").trim() || "InstaTenders",
          soNumber: record.soNumber || "",
          soDate: toDateInputValue(record.soDate),
          customer: customerName,
          customerName,
          customerId,
          username:
            record.username ||
            String(localStorage.getItem("name") || "").trim(),
          clientReference: record.clientReference || "",
          department: record.department || "",
        };

        const recordId = String(record._id || record.id || id);
        setGoId(recordId);
        setGoStatus(String(record.status || "draft").toLowerCase());
        setForm(nextForm);
        setSavedHeaderSnapshot(getHeaderSnapshot(nextForm));

        const apiMaterials = Array.isArray(record.materials)
          ? record.materials
          : [];
        const mappedMaterials = apiMaterials.map((m, index) => {
            const code = cleanDash(m.materialCode);
            const description = m.materialDescription || "";
            const outwards = qtyString(m.goodsOutwards);
            const available = qtyString(m.availableStock);
            const inventoryUpdated = Boolean(m.inventoryUpdated);
            const sourceType =
              String(m.sourceType || "").toLowerCase() === "so" ||
              m.so_id ||
              m.soId
                ? "so"
                : "material";
            return createMaterialRow({
              sourceKey:
                m._id ||
                m.id ||
                `${code}|${description}|${index}`,
              sourceType,
              so_id: extractEntityId(m.so_id || m.soId),
              soNumber: m.soNumber || record.soNumber || "",
              materialCode: code,
              materialDescription: description,
              hsnCode: cleanDash(m.hsnCode || m.hsn),
              uom: cleanDash(m.uom),
              soQuantity: qtyString(m.soQuantity),
              availableStock: available,
              goodsOutwards: outwards,
              inventoryUpdated,
              soBalanceQuantity: qtyString(m.soBalanceQuantity),
            });
          });
        setMaterials(mappedMaterials);
        materialsRef.current = mappedMaterials;

        if (record.soNumber) {
          await loadSoBalanceLines(record.soNumber, recordId);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Load goods outwards for edit error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(errData?.message || "Failed to load goods outwards", {
          autoClose: 3000,
        });
        navigate("/goodsOutwards");
      } finally {
        if (!cancelled) setIsLoadingGo(false);
      }
    };

    fetchGoForEdit();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, loadSoBalanceLines]);

  const searchSalesOrders = useCallback(async (term) => {
    const q = String(term || "").trim();
    if (!q) {
      setSoResults([]);
      return;
    }
    setIsSearchingSo(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-sales-order",
        page: 1,
        limit: 20,
        search: q,
        status: "Active",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setSoResults(extractSoList(responseData));
      } else {
        setSoResults([]);
      }
    } catch (error) {
      console.error("SO search error:", error);
      setSoResults([]);
    } finally {
      setIsSearchingSo(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = String(soSearchTerm || "").trim();
      if (showSoDropdown && q.length >= 1) {
        searchSalesOrders(q);
      } else if (!q) {
        setSoResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [soSearchTerm, showSoDropdown, searchSalesOrders]);

  const clearSoSelection = () => {
    setSoMaterials([]);
    setSelectedSoKeys([]);
    setSoMaterialSearchApplied("");
    setSoMaterialResultsOpen(true);
  };

  const applySoSelection = async (so) => {
    const soId = so?.id || so?._id;
    if (!soId) {
      toast.error("Selected SO has no id", { autoClose: 2500 });
      return;
    }

    const previousSoNumber = String(form.soNumber || "").trim();
    const nextSoNumber = String(so.cpoNumber || "").trim();
    const soChanged = Boolean(previousSoNumber && previousSoNumber !== nextSoNumber);

    setIsLoadingSoDetail(true);
    setShowSoDropdown(false);
    setSoSearchTerm(so.cpoNumber || "");

    try {
      const detailRes = await axios.post(API_URL, {
        action: "get-sales-order-by-id",
        id: soId,
      });
      const detailData = normalizeApiPayload(parseApiResponse(detailRes.data));
      if (!isApiSuccess(detailData)) {
        toast.error(detailData?.message || "Failed to load SO details", { autoClose: 3000 });
        return;
      }

      const detail = detailData?.data || {};
      const soNumber = detail.cpoNumber || so.cpoNumber || "";
      const customerIdObj =
        detail.customerId && typeof detail.customerId === "object" ? detail.customerId : null;
      const customerName =
        customerIdObj?.customerName || detail.customerName || so.customerName || "";
      const resolvedCustomerId = String(
        customerIdObj?._id || customerIdObj?.id || detail.customerId || "",
      ).trim();
      const soDate = toDateInputValue(detail.cpoDate || "");
      const soLines = Array.isArray(detail.materials) ? detail.materials : [];
      const currentGoId = String(goId || id || "").trim();

      let mappedMats = [];
      if (soLines.length && soNumber) {
        const balanceRes = await axios.post(API_URL, {
          action: "get-so-material-balance",
          soNumber,
          materials: soLines.map(buildSoBalanceRequestLine),
        });
        const balanceData = normalizeApiPayload(parseApiResponse(balanceRes.data));
        if (isApiSuccess(balanceData)) {
          const balanceMats = Array.isArray(balanceData?.data?.materials)
            ? balanceData.data.materials
            : [];
          mappedMats = balanceMats.map((m, i) =>
            mapSoBalanceMaterial(m, i, { excludeGoId: currentGoId }),
          );
        } else {
          console.warn("get-so-material-balance failed:", balanceData?.message);
          toast.warning(
            balanceData?.message || "Could not load SO material balance",
            { autoClose: 3500 },
          );
        }
      } else if (!soLines.length) {
        toast.warning("This sales order has no material lines", { autoClose: 3000 });
      }

      setForm((prev) => ({
        ...prev,
        soNumber,
        soDate,
        customer: customerName,
        customerName,
        customerId: resolvedCustomerId,
      }));
      setSoMaterials(mappedMats);
      setSelectedSoKeys([]);
      setSoMaterialSearchApplied("");
      setSoMaterialResultsOpen(true);
      if (soChanged) {
        setMaterials([]);
      }
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.soNumber;
        delete next.customerName;
        delete next.customerId;
        delete next.materials;
        return next;
      });
    } catch (error) {
      console.error("SO detail/balance error:", error);
      toast.error("Failed to load SO details", { autoClose: 3000 });
    } finally {
      setIsLoadingSoDetail(false);
    }
  };

  const isSoMaterialAdded = useCallback(
    (m) => materials.some((row) => row.sourceKey === m.sourceKey),
    [materials],
  );

  const isSoMaterialClosed = (m) => {
    const bal = parseFloat(m.soBalanceQuantity);
    return Number.isFinite(bal) && bal <= 0;
  };

  const filteredSoMaterials = useMemo(() => {
    const q = String(soMaterialSearchApplied || "").trim().toLowerCase();
    if (!q) return soMaterials;
    return soMaterials.filter((m) => {
      const code = String(m.materialCode || "").toLowerCase();
      const desc = String(m.materialDescription || "").toLowerCase();
      const hsn = String(m.hsnCode || "").toLowerCase();
      return code.includes(q) || desc.includes(q) || hsn.includes(q);
    });
  }, [soMaterials, soMaterialSearchApplied]);

  const getSelectableSoKeys = useCallback(
    () =>
      filteredSoMaterials
        .filter((m) => !isSoMaterialAdded(m) && !isSoMaterialClosed(m))
        .map((m) => m.sourceKey),
    [filteredSoMaterials, isSoMaterialAdded],
  );

  const countPendingSoSelection = () =>
    getSelectableSoKeys().filter((key) => selectedSoKeys.includes(key)).length;

  const countAddedInCurrentSoResults = () =>
    filteredSoMaterials.filter((m) => isSoMaterialAdded(m)).length;

  const toggleSoMaterialKey = (sourceKey) => {
    setSelectedSoKeys((prev) =>
      prev.includes(sourceKey) ? prev.filter((k) => k !== sourceKey) : [...prev, sourceKey],
    );
  };

  const toggleSelectAllSoMaterials = () => {
    const selectable = getSelectableSoKeys();
    const allSelected =
      selectable.length > 0 && selectable.every((key) => selectedSoKeys.includes(key));
    if (allSelected) {
      setSelectedSoKeys((prev) => prev.filter((key) => !selectable.includes(key)));
    } else {
      setSelectedSoKeys((prev) => Array.from(new Set([...prev, ...selectable])));
    }
  };

  const addSelectedSoMaterials = () => {
    const selectable = getSelectableSoKeys();
    const toAdd = filteredSoMaterials.filter(
      (m) => selectedSoKeys.includes(m.sourceKey) && selectable.includes(m.sourceKey),
    );
    if (!toAdd.length) {
      toast.info("Select one or more SO material lines to add", { autoClose: 2500 });
      return;
    }
    setMaterials((prev) => [...prev, ...toAdd.map(buildGoRowFromSoLine)]);
    setSelectedSoKeys((prev) => prev.filter((key) => !toAdd.some((m) => m.sourceKey === key)));
    setFieldErrors((prev) => {
      if (!prev.materials) return prev;
      const next = { ...prev };
      delete next.materials;
      return next;
    });
  };

  const fetchMaterialMasterCatalog = useCallback(
    async ({ force = false } = {}) => {
      if (!force && materialMasterLoadedRef.current) return;
      setIsLoadingMaterialMaster(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-all-material",
          page: 1,
          limit: 5000,
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (isApiSuccess(responseData)) {
          setMaterialMasterCatalog(extractMaterialList(responseData));
          materialMasterLoadedRef.current = true;
        } else {
          setMaterialMasterCatalog([]);
        }
      } catch (error) {
        console.error("Material master catalog error:", error);
        setMaterialMasterCatalog([]);
      } finally {
        setIsLoadingMaterialMaster(false);
      }
    },
    [],
  );

  const updateMaterialCodeDropdownPosition = useCallback((rowKey) => {
    const el = materialCodeInputRefs.current[rowKey];
    if (!el) {
      setMaterialCodeDropdownRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setMaterialCodeDropdownRect({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  }, []);

  const openMaterialCodeDropdown = useCallback(
    (rowKey) => {
      setMaterialCodeDropdownRowKey(rowKey);
      requestAnimationFrame(() => updateMaterialCodeDropdownPosition(rowKey));
      fetchMaterialMasterCatalog();
    },
    [updateMaterialCodeDropdownPosition, fetchMaterialMasterCatalog],
  );

  useEffect(() => {
    if (!materialCodeDropdownRowKey) return undefined;
    const onReposition = () =>
      updateMaterialCodeDropdownPosition(materialCodeDropdownRowKey);
    onReposition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [materialCodeDropdownRowKey, updateMaterialCodeDropdownPosition]);

  const getFilteredMaterialMaster = useCallback(
    (searchTerm) => {
      const q = String(searchTerm || "").trim().toLowerCase();
      if (!q) return [];
      return materialMasterCatalog
        .filter((m) => {
          const code = String(m.materialCode || m.code || "").toLowerCase();
          const name = String(
            m.productName || m.materialDescription || m.description || "",
          ).toLowerCase();
          const hsn = String(m.hsnCode || m.hsn || "").toLowerCase();
          return code.includes(q) || name.includes(q) || hsn.includes(q);
        })
        .slice(0, 40);
    },
    [materialMasterCatalog],
  );

  const fetchStockForRow = useCallback(async (row, { silent = false } = {}) => {
    const code = String(row.materialCode || "").trim();
    if (!code) return;

    setStockLoadingKeys((prev) => ({ ...prev, [row.rowKey]: true }));
    try {
      const response = await axios.post(API_URL, {
        action: "get-inventory-by-id",
        materialCode: code,
        id: code,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        const inventory = pickInventoryRecord(responseData);
        const liveStock = pickStockQuantity(inventory) || "0";
        setMaterials((prev) => {
          const updated = prev.map((m) => {
            if (m.rowKey !== row.rowKey) return m;
            const outwards = parseFloat(m.goodsOutwards);
            const live = parseFloat(liveStock);
            // If inventory was already deducted for this GO line, credit qty back for edit
            const stock =
              m.inventoryUpdated &&
              Number.isFinite(live) &&
              Number.isFinite(outwards)
                ? String(live + outwards)
                : liveStock;
            return { ...m, availableStock: stock };
          });
          const current = updated.find((m) => m.rowKey === row.rowKey);
          if (current) {
            const outwards = parseFloat(current.goodsOutwards);
            const available = parseFloat(current.availableStock);
            const errorKey = `mat_${row.rowKey}_goodsOutwards`;
            setFieldErrors((prevErrors) => {
              const next = { ...prevErrors };
              if (
                Number.isFinite(outwards) &&
                Number.isFinite(available) &&
                outwards > available
              ) {
                next[errorKey] = "Cannot exceed available stock";
              } else if (next[errorKey] === "Cannot exceed available stock") {
                delete next[errorKey];
              }
              return next;
            });
          }
          return updated;
        });
      } else if (!silent) {
        toast.error(responseData?.message || "Inventory not found", {
          autoClose: 2500,
        });
      }
    } catch (error) {
      console.error("Inventory fetch error:", error);
      if (!silent) {
        toast.error("Failed to fetch inventory stock", { autoClose: 2500 });
      }
    } finally {
      setStockLoadingKeys((prev) => {
        const next = { ...prev };
        delete next[row.rowKey];
        return next;
      });
    }
  }, []);

  const fetchStockForAllRows = useCallback(
    async (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      await Promise.all(
        list.map((row) => fetchStockForRow(row, { silent: true })),
      );
    },
    [fetchStockForRow],
  );

  const mapCatalogMaterial = (m) => {
    const code = cleanDash(m?.materialCode || m?.code);
    const description =
      m?.productName || m?.materialDescription || m?.description || "";
    return {
      sourceKey: m?._id || m?.id || `${code}|${description}`,
      sourceType: "material",
      materialCode: code,
      materialDescription: description,
      hsnCode: cleanDash(m?.hsnCode || m?.hsn),
      uom: cleanDash(m?.uom || m?.unit),
      soQuantity: "",
      availableStock: "",
      goodsOutwards: "",
    };
  };

  const applyCatalogMaterialToRow = (rowKey, material) => {
    const mapped = mapCatalogMaterial(material);
    if (!mapped.materialCode) {
      toast.error("Selected material has no code", { autoClose: 2500 });
      return;
    }
    setMaterials((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
              ...row,
              ...mapped,
              sourceType: "material",
              soQuantity: "",
            }
          : row,
      ),
    );
    setMaterialCodeDropdownRowKey(null);
    setMaterialCodeDropdownRect(null);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.materials;
      delete next[`mat_${rowKey}_materialCode`];
      delete next[`mat_${rowKey}_materialDescription`];
      return next;
    });
    fetchStockForRow(
      { rowKey, materialCode: mapped.materialCode, goodsOutwards: "" },
      { silent: true },
    );
  };

  const canAddStandaloneMaterials = useMemo(() => {
    const hasCustomer = Boolean(
      String(form.customerId || "").trim() ||
        String(form.customerName || form.customer || "").trim(),
    );
    return hasCustomer;
  }, [form.customerId, form.customerName, form.customer]);

  const addStandaloneMaterialRow = () => {
    if (!canAddStandaloneMaterials) {
      toast.info("Select a customer first", { autoClose: 2000 });
      return;
    }
    setMaterials((prev) => [
      ...prev,
      createMaterialRow({ sourceType: "material" }),
    ]);
    setFieldErrors((prev) => {
      if (!prev.materials) return prev;
      const next = { ...prev };
      delete next.materials;
      return next;
    });
    fetchMaterialMasterCatalog();
  };

  const removeMaterialRow = (rowKey) => {
    setMaterials((prev) => prev.filter((row) => row.rowKey !== rowKey));
  };

  const updateMaterialRow = (rowKey, name, value) => {
    let qtyErrors = {};

    setMaterials((prev) =>
      prev.map((row) => {
        if (row.rowKey !== rowKey) return row;
        const next = { ...row, [name]: value };

        if (name === "goodsOutwards") {
          const outwards = parseFloat(next.goodsOutwards);
          const available = parseFloat(next.availableStock);

          if (next.goodsOutwards === "" || next.goodsOutwards == null) {
            // leave empty until user enters a value
          } else if (!Number.isFinite(outwards) || outwards <= 0) {
            qtyErrors[`mat_${rowKey}_goodsOutwards`] = "Invalid quantity";
          } else if (
            Number.isFinite(available) &&
            outwards > available
          ) {
            qtyErrors[`mat_${rowKey}_goodsOutwards`] =
              "Cannot exceed available stock";
          }
        }

        return next;
      }),
    );

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.materials;
      delete next[`mat_${rowKey}_${name}`];
      if (name === "goodsOutwards") {
        delete next[`mat_${rowKey}_goodsOutwards`];
      }
      return { ...next, ...qtyErrors };
    });
  };

  const validateDetails = () => {
    const errors = {};
    if (!String(form.goSummary || "").trim()) {
      errors.goSummary = "GO summary is required";
    }
    if (!String(form.invoiceNumber || "").trim()) {
      errors.invoiceNumber = "Invoice number is required";
    }
    if (!String(form.whoseStock || "").trim()) {
      errors.whoseStock = "Whose stock is required";
    }
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length) {
      toast.error("Please complete GO Details", { autoClose: 2500 });
      return false;
    }
    return true;
  };

  const hasSoLinkedMaterials = useMemo(
    () =>
      materials.some(
        (row) =>
          String(row.sourceType || "").toLowerCase() === "so" ||
          Boolean(row.so_id || row.soId || row.soNumber),
      ),
    [materials],
  );

  const validateMaterials = () => {
    const headerErrors = {};
    if (hasSoLinkedMaterials && !String(form.soNumber || "").trim()) {
      headerErrors.soNumber = "SO number is required";
    }
    const customerName = String(
      form.customerName || form.customer || "",
    ).trim();
    if (!customerName && !String(form.customerId || "").trim()) {
      headerErrors.customerName = "Customer is required";
    }
    if (Object.keys(headerErrors).length) {
      setFieldErrors((prev) => ({ ...prev, ...headerErrors }));
      toast.error("Please complete customer / SO details on the Material tab", {
        autoClose: 2500,
      });
      return false;
    }

    setFieldErrors((prev) => {
      if (!prev.soNumber && !prev.customerName && !prev.customerId) return prev;
      const next = { ...prev };
      delete next.soNumber;
      delete next.customerName;
      delete next.customerId;
      delete next.customer;
      return next;
    });

    if (!materials.length) {
      setFieldErrors((prev) => ({
        ...prev,
        materials: "Add at least one material line",
      }));
      toast.error("Add at least one material line", { autoClose: 2500 });
      return false;
    }
    const errors = {};
    materials.forEach((row) => {
      if (!String(row.materialCode || "").trim()) {
        errors[`mat_${row.rowKey}_materialCode`] = "Required";
      }
      const outwards = parseFloat(row.goodsOutwards);
      if (!Number.isFinite(outwards) || outwards <= 0) {
        errors[`mat_${row.rowKey}_goodsOutwards`] = "Invalid quantity";
      }
      const available = parseFloat(row.availableStock);
      if (
        Number.isFinite(outwards) &&
        Number.isFinite(available) &&
        outwards > available
      ) {
        errors[`mat_${row.rowKey}_goodsOutwards`] =
          "Cannot exceed available stock";
      }
    });
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length) {
      toast.error("Please fix material quantity errors", { autoClose: 2500 });
      return false;
    }
    return true;
  };

  const hasInvalidMaterialQuantities = useMemo(() => {
    if (!materials.length) return false;
    return materials.some((row) => {
      const outwards = parseFloat(row.goodsOutwards);
      const available = parseFloat(row.availableStock);
      if (!Number.isFinite(outwards) || outwards <= 0) return true;
      if (Number.isFinite(available) && outwards > available) return true;
      return false;
    });
  }, [materials]);

  const buildHeaderFields = () => {
    const fields = {
      goSummary: String(form.goSummary || "").trim(),
      invoiceNumber: String(form.invoiceNumber || "").trim(),
      whoseStock: String(form.whoseStock || "").trim(),
    };
    if (form.goDate) fields.goDate = form.goDate;
    if (form.invoiceDate) fields.invoiceDate = form.invoiceDate;
    if (form.goNumber) fields.goNumber = String(form.goNumber).trim();
    if (String(form.soNumber || "").trim()) {
      fields.soNumber = String(form.soNumber).trim();
    }
    if (form.soDate) fields.soDate = form.soDate;
    const customerName = String(
      form.customerName || form.customer || "",
    ).trim();
    if (customerName) {
      fields.customer = customerName;
      fields.customerName = customerName;
    }
    if (String(form.customerId || "").trim()) {
      fields.customerId = String(form.customerId).trim();
    }
    if (String(form.username || "").trim()) {
      fields.username = String(form.username).trim();
    }
    if (String(form.clientReference || "").trim()) {
      fields.clientReference = String(form.clientReference).trim();
    }
    if (String(form.department || "").trim()) {
      fields.department = String(form.department).trim();
    }
    return fields;
  };

  const buildMaterialsPayload = () => {
    const whoseStock = String(form.whoseStock || "").trim();
    return materials.map((row) => {
      const availableStock = toNumberOrEmpty(row.availableStock);
      const goodsOutwards = toNumberOrEmpty(row.goodsOutwards);
      const updatedAvailableStock = toNumberOrEmpty(
        computeUpdatedAvailableStock(row),
      );
      const soQuantity = toNumberOrEmpty(row.soQuantity);
      const item = {
        materialCode: String(row.materialCode || "").trim(),
        materialDescription: String(row.materialDescription || "").trim(),
        uom: String(row.uom || "").trim(),
        whoseStock,
      };
      const hsn = String(row.hsnCode || "").trim();
      if (hsn) item.hsnCode = hsn;
      if (soQuantity !== "") item.soQuantity = soQuantity;
      if (availableStock !== "") item.availableStock = availableStock;
      if (goodsOutwards !== "") item.goodsOutwards = goodsOutwards;
      if (updatedAvailableStock !== "") {
        item.updatedAvailableStock = updatedAvailableStock;
      }
      return item;
    });
  };

  const applyGoMetaFromResponse = (responseData) => {
    const nextId = extractCreatedGoId(responseData);
    if (nextId) setGoId(String(nextId));
    const goNumber = extractGoNumber(responseData);
    if (goNumber) {
      setForm((prev) => ({ ...prev, goNumber: String(goNumber) }));
    }
    const record = pickGoFromResponse(responseData);
    const nextStatus = record?.status ?? responseData?.data?.status;
    if (nextStatus) {
      setGoStatus(String(nextStatus).toLowerCase());
    }
    return nextId ? String(nextId) : null;
  };

  const saveDraftFromDetails = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return null;
    }

    if (!goId) {
      toast.error("Goods outwards id is missing", { autoClose: 2500 });
      return null;
    }

    if (!isHeaderDirty()) {
      return goId;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = goStatus === "active" ? "active" : "draft";
      const payload = {
        action: "update-goods-outwards",
        id: goId,
        status: statusToSave,
        ...buildHeaderFields(),
      };
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to update goods outwards",
          { autoClose: 3000 },
        );
        return null;
      }
      setSavedHeaderSnapshot(getHeaderSnapshot(form));
      toast.success(
        responseData?.message || "GO details updated successfully",
        { autoClose: 2000 },
      );
      return applyGoMetaFromResponse(responseData) || goId;
    } catch (error) {
      console.error("Update GO details error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating GO",
        { autoClose: 3000 },
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDraftWithMaterials = async ({ activate = false } = {}) => {
    if (!validateDetails()) {
      setActiveTab("details");
      return null;
    }
    if (!validateMaterials()) {
      setActiveTab(activate ? "quantities" : "material");
      return null;
    }

    if (!goId) {
      toast.error("Goods outwards id is missing", { autoClose: 2500 });
      return null;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = activate
        ? "active"
        : goStatus === "active"
          ? "active"
          : "draft";
      const payload = {
        action: "update-goods-outwards",
        id: goId,
        status: statusToSave,
        ...buildHeaderFields(),
        materials: buildMaterialsPayload(),
      };
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message ||
            (activate
              ? "Failed to activate goods outwards"
              : "Failed to update materials"),
          { autoClose: 3000 },
        );
        return null;
      }

      applyGoMetaFromResponse(responseData);
      setSavedHeaderSnapshot(getHeaderSnapshot(form));
      if (activate) {
        toast.success(
          responseData?.message || "Goods outwards activated successfully",
          { autoClose: 2500 },
        );
        navigate(`/goodsOutwardsView/${goId}`);
      }
      return goId;
    } catch (error) {
      console.error("Update GO materials error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating materials",
        { autoClose: 3000 },
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return;
    }
    if (!validateMaterials()) {
      setActiveTab("quantities");
      return;
    }
    if (!goId) {
      toast.error("Goods outwards id is missing", { autoClose: 2500 });
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = goStatus === "active" ? "active" : "draft";
      const payload = {
        action: "update-goods-outwards",
        id: goId,
        status: statusToSave,
        ...buildHeaderFields(),
        materials: buildMaterialsPayload(),
      };
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to update goods outwards",
          { autoClose: 3000 },
        );
        return;
      }

      applyGoMetaFromResponse(responseData);
      toast.success(
        responseData?.message || "Goods outwards updated successfully",
        { autoClose: 2500 },
      );
      navigate(`/goodsOutwardsView/${goId}`);
    } catch (error) {
      console.error("Update GO error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating goods outwards",
        { autoClose: 3000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToTab = async (tabId) => {
    if (tabId === "details") {
      setActiveTab("details");
      return;
    }
    if (tabId === "material") {
      const saved = await saveDraftFromDetails();
      if (!saved) return;
      setActiveTab("material");
      return;
    }
    if (tabId === "quantities") {
      const updated = await updateDraftWithMaterials();
      if (!updated) return;
      setActiveTab("quantities");
      fetchStockForAllRows(materials);
    }
  };

  const handleNext = async () => {
    if (activeTab === "details") {
      const saved = await saveDraftFromDetails();
      if (!saved) return;
      setActiveTab("material");
      return;
    }
    if (activeTab === "material") {
      const updated = await updateDraftWithMaterials();
      if (!updated) return;
      setActiveTab("quantities");
      fetchStockForAllRows(materials);
    }
  };

  const handlePrevious = () => {
    if (activeTab === "quantities") setActiveTab("material");
    else if (activeTab === "material") setActiveTab("details");
  };

  const handleActivate = async () => {
    await updateDraftWithMaterials({ activate: true });
  };

  const tabClass = (tabId) =>
    `nav-link${activeTab === tabId ? " active" : ""}`;

  const cellClass = (rowKey, name) =>
    `form-control form-control-sm${
      fieldErrors[`mat_${rowKey}_${name}`] ? " border-danger" : ""
    }`;

  const renderMaterialsTable = ({ showQty = false, showStock = false } = {}) => {
    const colCount =
      6 + (showQty ? 1 : 0) + (showStock ? 1 : 0) + (showStock ? 0 : 1);

    return (
      <div
        className="table-responsive"
        style={
          materialCodeDropdownRowKey ? { overflow: "visible" } : undefined
        }
      >
        <table className="table table-bordered table-sm mb-0">
          <thead className="thead-light">
            <tr>
              <th style={{ minWidth: 130 }}>Material Code *</th>
              <th style={{ minWidth: 220 }}>Description *</th>
              <th style={{ minWidth: 90 }}>HSN</th>
              <th style={{ minWidth: 70 }}>UOM</th>
              <th style={{ minWidth: 100 }}>SO Qty</th>
              <th style={{ minWidth: 110 }}>Available Stock</th>
              {showQty && (
                <th style={{ minWidth: 120 }}>Goods Outwards *</th>
              )}
              {showStock && (
                <th style={{ minWidth: 140 }}>Updated Available</th>
              )}
              {!showStock && <th style={{ width: 60 }} />}
            </tr>
          </thead>
          <tbody>
            {!materials.length ? (
              <tr>
                <td colSpan={colCount} className="text-muted">
                  No materials added yet.{" "}
                  {canAddStandaloneMaterials
                    ? "Click Add to create a line, then search by material code."
                    : "Select a customer, then click Add to create a material line."}
                </td>
              </tr>
            ) : (
              materials.map((row) => {
                const standalone = isStandaloneMaterialRow(row);
                const showMaterialCodeSearch = standalone && !showStock;
                const filteredMaster = showMaterialCodeSearch
                  ? getFilteredMaterialMaster(row.materialCode)
                  : [];
                const showCodeDropdown =
                  showMaterialCodeSearch &&
                  materialCodeDropdownRowKey === row.rowKey &&
                  String(row.materialCode || "").trim().length >= 1;

                return (
                  <tr key={row.rowKey}>
                    <td style={{ overflow: "visible", position: "relative" }}>
                      {showMaterialCodeSearch ? (
                        <div
                          style={{ position: "relative" }}
                          data-go-material-code-search="true"
                        >
                          <input
                            ref={(el) => {
                              if (el) {
                                materialCodeInputRefs.current[row.rowKey] = el;
                              } else {
                                delete materialCodeInputRefs.current[row.rowKey];
                              }
                            }}
                            type="text"
                            className={cellClass(row.rowKey, "materialCode")}
                            value={row.materialCode}
                            placeholder="Search material code..."
                            onFocus={() => openMaterialCodeDropdown(row.rowKey)}
                            onChange={(e) => {
                              updateMaterialRow(
                                row.rowKey,
                                "materialCode",
                                e.target.value,
                              );
                              openMaterialCodeDropdown(row.rowKey);
                            }}
                            autoComplete="off"
                          />
                          {showCodeDropdown &&
                            materialCodeDropdownRect &&
                            createPortal(
                              <div
                                className="dropdown-menu show"
                                data-go-material-code-dropdown="true"
                                style={{
                                  display: "block",
                                  position: "fixed",
                                  top: materialCodeDropdownRect.top,
                                  left: materialCodeDropdownRect.left,
                                  width: materialCodeDropdownRect.width,
                                  maxHeight: 220,
                                  overflowY: "auto",
                                  zIndex: 3000,
                                  margin: 0,
                                }}
                              >
                                {isLoadingMaterialMaster ? (
                                  <span className="dropdown-item text-muted">
                                    Loading materials...
                                  </span>
                                ) : filteredMaster.length === 0 ? (
                                  <span className="dropdown-item text-muted">
                                    No materials found
                                  </span>
                                ) : (
                                  filteredMaster.map((m) => {
                                    const mapped = mapCatalogMaterial(m);
                                    return (
                                      <button
                                        key={mapped.sourceKey}
                                        type="button"
                                        className="dropdown-item"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() =>
                                          applyCatalogMaterialToRow(
                                            row.rowKey,
                                            m,
                                          )
                                        }
                                      >
                                        <strong>
                                          {mapped.materialCode || "-"}
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                          {mapped.materialDescription ||
                                            "No description"}
                                          {mapped.uom
                                            ? ` · ${mapped.uom}`
                                            : ""}
                                        </small>
                                      </button>
                                    );
                                  })
                                )}
                              </div>,
                              document.body,
                            )}
                        </div>
                      ) : (
                        toDisplay(row.materialCode)
                      )}
                    </td>
                    <td>{toDisplay(row.materialDescription)}</td>
                    <td>{toDisplay(row.hsnCode)}</td>
                    <td>{toDisplay(row.uom)}</td>
                    <td>
                      {showQty && !showStock ? (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className={cellClass(row.rowKey, "soQuantity")}
                          value={row.soQuantity}
                          onChange={(e) =>
                            updateMaterialRow(
                              row.rowKey,
                              "soQuantity",
                              e.target.value,
                            )
                          }
                        />
                      ) : (
                        toDisplay(row.soQuantity)
                      )}
                    </td>
                    <td>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: 6 }}
                      >
                        <span>
                          {stockLoadingKeys[row.rowKey]
                            ? "..."
                            : toDisplay(row.availableStock)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={!!stockLoadingKeys[row.rowKey]}
                          onClick={() => fetchStockForRow(row)}
                          title="Refresh stock"
                        >
                          <i className="fa-solid fa-rotate" />
                        </button>
                      </div>
                    </td>
                    {showQty && (
                      <td>
                        {showStock ? (
                          toDisplay(row.goodsOutwards)
                        ) : (
                          <>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              className={cellClass(row.rowKey, "goodsOutwards")}
                              value={row.goodsOutwards}
                              onChange={(e) =>
                                updateMaterialRow(
                                  row.rowKey,
                                  "goodsOutwards",
                                  e.target.value,
                                )
                              }
                            />
                            {fieldErrors[
                              `mat_${row.rowKey}_goodsOutwards`
                            ] && (
                              <small className="text-danger d-block mt-1">
                                {
                                  fieldErrors[
                                    `mat_${row.rowKey}_goodsOutwards`
                                  ]
                                }
                              </small>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    {showStock && (
                      <td>{toDisplay(computeUpdatedAvailableStock(row))}</td>
                    )}
                    {!showStock && (
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          title="Remove"
                          onClick={() => removeMaterialRow(row.rowKey)}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoadingGo) {
    return (
      <div className="content-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center py-5">
            <PulseLoader color="#1199B2" size={10} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Goods Outwards</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/goodsOutwards">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    {TABS.map((tab) => (
                      <li className="nav-item" key={tab.id}>
                        <button
                          type="button"
                          className={tabClass(tab.id)}
                          onClick={() =>
                            tab.id === "details"
                              ? setActiveTab("details")
                              : goToTab(tab.id)
                          }
                        >
                          {tab.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body">
                  {activeTab === "details" && (
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>GO Number</label>
                          <input
                            type="text"
                            className="form-control"
                            value={form.goNumber || "—"}
                            readOnly
                            disabled
                          />
                          <small className="text-muted">
                            GO number is locked for this record
                          </small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>GO Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.goDate}
                            onChange={(e) =>
                              updateForm("goDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>
                            GO Summary <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control${
                              fieldErrors.goSummary ? " border-danger" : ""
                            }`}
                            value={form.goSummary}
                            onChange={(e) =>
                              updateForm("goSummary", e.target.value)
                            }
                            placeholder="Issue materials for SO"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>
                            Invoice Number{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control${
                              fieldErrors.invoiceNumber ? " border-danger" : ""
                            }`}
                            value={form.invoiceNumber}
                            onChange={(e) =>
                              updateForm("invoiceNumber", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>Invoice Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.invoiceDate}
                            onChange={(e) =>
                              updateForm("invoiceDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>
                            Whose Stock <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-control${
                              fieldErrors.whoseStock ? " border-danger" : ""
                            }`}
                            value={form.whoseStock}
                            onChange={(e) =>
                              updateForm("whoseStock", e.target.value)
                            }
                          >
                            <option value="">Select whose stock</option>
                            {WHOSE_STOCK_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "material" && (
                    <>
                      <div className="row align-items-end mb-3">
                        <div className="col-md-4" ref={soSearchRef}>
                          <div className="form-group mb-0">
                            <label>SO Number</label>
                            <div style={{ position: "relative" }}>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className={`form-control${
                                    fieldErrors.soNumber ? " border-danger" : ""
                                  }`}
                                  value={soSearchTerm || form.soNumber}
                                  placeholder="Optional — search SO number..."
                                  onFocus={() => {
                                    if (
                                      String(soSearchTerm || "").trim().length >= 1
                                    ) {
                                      setShowSoDropdown(true);
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setSoSearchTerm(value);
                                    setShowSoDropdown(
                                      String(value).trim().length >= 1,
                                    );
                                    if (!String(value).trim()) {
                                      setSoResults([]);
                                    }
                                    if (
                                      form.soNumber &&
                                      String(value).trim() !==
                                        String(form.soNumber).trim()
                                    ) {
                                      updateForm("soNumber", "");
                                      updateForm("soDate", "");
                                      clearSoSelection();
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      const q = String(
                                        soSearchTerm || "",
                                      ).trim();
                                      if (q) {
                                        setShowSoDropdown(true);
                                        searchSalesOrders(q);
                                      }
                                    }
                                  }}
                                  autoComplete="off"
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    disabled={isSearchingSo || isLoadingSoDetail}
                                    onClick={() => {
                                      const q = String(
                                        soSearchTerm || "",
                                      ).trim();
                                      if (q) {
                                        setShowSoDropdown(true);
                                        searchSalesOrders(q);
                                      }
                                    }}
                                  >
                                    {isSearchingSo || isLoadingSoDetail ? (
                                      "..."
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              {showSoDropdown &&
                                String(soSearchTerm || "").trim().length >=
                                  1 && (
                                  <div
                                    className="dropdown-menu show w-100"
                                    style={{
                                      display: "block",
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      marginTop: 0,
                                      maxHeight: 240,
                                      overflowY: "auto",
                                      zIndex: 1050,
                                    }}
                                  >
                                    {isSearchingSo ? (
                                      <span className="dropdown-item text-muted">
                                        Searching...
                                      </span>
                                    ) : soResults.length === 0 ? (
                                      <span className="dropdown-item text-muted">
                                        No sales orders found
                                      </span>
                                    ) : (
                                      soResults.map((so) => (
                                        <button
                                          key={so.id || so._id}
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => applySoSelection(so)}
                                        >
                                          <strong>
                                            {so.cpoNumber || "-"}
                                          </strong>
                                          <br />
                                          <small className="text-muted">
                                            {so.customerName ||
                                              "No customer"}
                                          </small>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                            </div>
                            {isLoadingSoDetail && (
                              <small className="text-muted">
                                Loading SO materials...
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-0">
                            <label>SO Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={form.soDate}
                              onChange={(e) =>
                                updateForm("soDate", e.target.value)
                              }
                              disabled={Boolean(form.soNumber)}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-0">
                            <label>
                              Customer <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-control${
                                fieldErrors.customerName ||
                                fieldErrors.customerId
                                  ? " border-danger"
                                  : ""
                              }`}
                              value={form.customerId || ""}
                              onChange={(e) =>
                                handleCustomerChange(e.target.value)
                              }
                              disabled={isLoadingCustomers || Boolean(form.soNumber)}
                            >
                              <option value="">
                                {isLoadingCustomers
                                  ? "Loading customers..."
                                  : "Select customer"}
                              </option>
                              {form.customerId &&
                                !customers.some(
                                  (c) => c._id === form.customerId,
                                ) && (
                                  <option value={form.customerId}>
                                    {form.customerName ||
                                      form.customer ||
                                      form.customerId}
                                  </option>
                                )}
                              {customers.map((customer) => (
                                <option key={customer._id} value={customer._id}>
                                  {customer.customerName}
                                </option>
                              ))}
                            </select>
                            {(fieldErrors.customerName ||
                              fieldErrors.customerId) && (
                              <small className="text-danger d-block mt-1">
                                {fieldErrors.customerName ||
                                  fieldErrors.customerId}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>

                      {!form.soNumber ? (
                        canAddStandaloneMaterials ? (
                          <div className="alert alert-light border mb-3">
                            Standalone GO (no SO) — add material lines manually
                            for <strong>{form.customerName || form.customer}</strong>.
                          </div>
                        ) : (
                          <div className="alert alert-light border mb-3">
                            Select a Sales Order to load material lines, or
                            select a customer to create a standalone GO.
                          </div>
                        )
                      ) : soMaterials.length === 0 ? (
                        <div className="alert alert-light border mb-3">
                          {isLoadingSoDetail
                            ? "Loading SO materials..."
                            : "No material lines on this SO"}
                        </div>
                      ) : (
                        <div
                          className="mb-3"
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            className="d-flex justify-content-between align-items-center flex-wrap"
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#f8f9fa",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setSoMaterialResultsOpen((open) => !open)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSoMaterialResultsOpen((open) => !open);
                              }
                            }}
                          >
                            <span
                              className="d-flex align-items-center"
                              style={{
                                fontWeight: 600,
                                fontSize: "13px",
                                gap: "8px",
                              }}
                            >
                              <i
                                className={`fa-solid ${
                                  soMaterialResultsOpen
                                    ? "fa-chevron-up"
                                    : "fa-chevron-down"
                                }`}
                                style={{ width: "14px", color: "#003366" }}
                                aria-hidden
                              />
                              SO material results —{" "}
                              {countPendingSoSelection()} to add,{" "}
                              {countAddedInCurrentSoResults()} already added
                            </span>
                            {soMaterialResultsOpen && (
                              <button
                                type="button"
                                className="btn btn-sm btn-blue"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSelectedSoMaterials();
                                }}
                                disabled={countPendingSoSelection() === 0}
                              >
                                Add
                              </button>
                            )}
                          </div>

                          {soMaterialResultsOpen && (
                            <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor: "#003366",
                                      color: "#fff",
                                    }}
                                  >
                                    <th style={{ fontSize: "11px", width: "36px" }}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          getSelectableSoKeys().length > 0 &&
                                          getSelectableSoKeys().every((key) =>
                                            selectedSoKeys.includes(key),
                                          )
                                        }
                                        onChange={toggleSelectAllSoMaterials}
                                        title="Select all not yet added"
                                      />
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Material Code
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Material Description
                                    </th>
                                    <th style={{ fontSize: "11px" }}>HSN</th>
                                    <th style={{ fontSize: "11px" }}>UOM</th>
                                    <th style={{ fontSize: "11px" }}>SO Qty</th>
                                    <th style={{ fontSize: "11px" }}>
                                      Available Stock
                                    </th>
                                    <th style={{ fontSize: "11px" }}>Balance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredSoMaterials.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="8"
                                        className="text-center text-muted"
                                      >
                                        No materials match your search
                                      </td>
                                    </tr>
                                  ) : (
                                    filteredSoMaterials.map((m) => {
                                      const isAdded = isSoMaterialAdded(m);
                                      const isClosed = isSoMaterialClosed(m);
                                      const isSelected = selectedSoKeys.includes(
                                        m.sourceKey,
                                      );
                                      const reservations = Array.isArray(
                                        m.reservations,
                                      )
                                        ? m.reservations
                                        : [];
                                      const hasReservations =
                                        reservations.length > 0;
                                      return (
                                        <React.Fragment key={m.sourceKey}>
                                          <tr
                                            style={{
                                              backgroundColor: isAdded
                                                ? "#e8f5e9"
                                                : isClosed
                                                  ? "#f5f5f5"
                                                  : isSelected
                                                    ? "#e8f4f8"
                                                    : undefined,
                                              opacity: isClosed ? 0.75 : 1,
                                            }}
                                          >
                                            <td className="text-center">
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={isAdded || isClosed}
                                                onChange={() =>
                                                  toggleSoMaterialKey(
                                                    m.sourceKey,
                                                  )
                                                }
                                                title={
                                                  isAdded
                                                    ? "Already added to GO materials"
                                                    : isClosed
                                                      ? "No remaining balance"
                                                      : ""
                                                }
                                              />
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.materialCode)}
                                              {m.hasDraftReservation && (
                                                <span
                                                  className="badge badge-warning ml-1"
                                                  style={{ fontSize: "9px" }}
                                                  title="Held by another draft GO"
                                                >
                                                  Draft GO
                                                </span>
                                              )}
                                            </td>
                                            <td
                                              style={{
                                                fontSize: "12px",
                                                maxWidth: "180px",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {toDisplay(m.materialDescription)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.hsnCode)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.uom)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.soQuantity)}
                                              {isAdded && (
                                                <span
                                                  className="badge badge-success ml-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Added
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.availableStock)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              <strong>
                                                {toDisplay(
                                                  m.soBalanceQuantity,
                                                )}
                                              </strong>
                                              {isClosed && (
                                                <span
                                                  className="badge badge-secondary ml-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Closed
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                          {hasReservations && (
                                            <tr>
                                              <td
                                                colSpan="8"
                                                style={{
                                                  backgroundColor: "#fff8e6",
                                                  padding: "8px 12px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    marginBottom: "6px",
                                                    color: "#6c4a00",
                                                  }}
                                                >
                                                  Draft GO reservations (
                                                  {reservations.length})
                                                </div>
                                                <table
                                                  className="table table-sm table-bordered mb-0"
                                                  style={{ background: "#fff" }}
                                                >
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        backgroundColor:
                                                          "#f0e6d2",
                                                      }}
                                                    >
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        GO Number
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        GO Date
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Status
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Goods Outwards
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Created By
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {reservations.map((r) => (
                                                      <tr
                                                        key={r.key}
                                                        style={{
                                                          backgroundColor:
                                                            "#fff3cd",
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {r.goId ? (
                                                            <Link
                                                              to={`/goodsOutwardsEdit/${r.goId}`}
                                                              style={{
                                                                color:
                                                                  "#003366",
                                                                fontWeight: 600,
                                                              }}
                                                            >
                                                              {toDisplay(
                                                                r.goNumber,
                                                              )}
                                                            </Link>
                                                          ) : (
                                                            toDisplay(
                                                              r.goNumber,
                                                            )
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {formatReservationDate(
                                                            r.goDate,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          <span
                                                            className="badge badge-warning"
                                                            style={{
                                                              fontSize: "9px",
                                                              textTransform:
                                                                "capitalize",
                                                            }}
                                                          >
                                                            {toDisplay(
                                                              r.status,
                                                            )}
                                                          </span>
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.goodsOutwards,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.createdByName,
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">
                          GO Materials ({materials.length})
                        </h5>
                        <div
                          className="d-flex align-items-center"
                          style={{ gap: 8 }}
                        >
                          {fieldErrors.materials && (
                            <small className="text-danger">
                              {fieldErrors.materials}
                            </small>
                          )}
                          {canAddStandaloneMaterials && (
                            <button
                              type="button"
                              className="btn btn-sm btn-blue"
                              onClick={addStandaloneMaterialRow}
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                      {renderMaterialsTable({ showQty: true, showStock: false })}
                    </>
                  )}

                  {activeTab === "quantities" && (
                    <>
                      <h5 className="mb-2">Quantities & Stock</h5>
                      {renderMaterialsTable({ showQty: true, showStock: true })}
                      <small className="text-muted d-block mt-2">
                        Available stock is loaded from inventory. Updated
                        available = Available − Goods outwards.
                      </small>
                    </>
                  )}
                </div>

                <div className="card-footer d-flex justify-content-between">
                  <div>
                    {activeTab !== "details" && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handlePrevious}
                        disabled={isSubmitting}
                      >
                        Previous
                      </button>
                    )}
                  </div>
                  <div>
                    {activeTab !== "quantities" ? (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleNext}
                        disabled={
                          isSubmitting ||
                          (activeTab === "material" &&
                            (hasInvalidMaterialQuantities || !materials.length))
                        }
                        title={
                          activeTab === "material" &&
                          hasInvalidMaterialQuantities
                            ? "Fix quantity errors before continuing"
                            : undefined
                        }
                      >
                        {isSubmitting ? "Saving..." : "Next"}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={handleUpdate}
                          disabled={
                            isSubmitting || hasInvalidMaterialQuantities
                          }
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </button>
                        {goStatus !== "active" && (
                          <button
                            type="button"
                            className="btn btn-blue ml-2"
                            onClick={handleActivate}
                            disabled={
                              isSubmitting || hasInvalidMaterialQuantities
                            }
                          >
                            {isSubmitting ? "Activating..." : "Activate"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoodsOutwardsEdit;